---
title: How RAG works here
summary: Why answers are grounded in your documents, and what "retrieval" and "citations" mean.
visibility: public
audience: author-admin
section: Concepts
order: 30
last_verified: 2026-06-19
owner: docs
---

# How RAG works here

Every expert persona answers using **RAG** — Retrieval-Augmented Generation. Instead of
relying only on what a language model learned during training, the persona first
*retrieves* relevant passages from your own documents and then writes its answer using
them. That's what makes replies specific to your business and lets the persona show its
sources.

## The shape of a single answer

1. **You ask a question.** Your message is sent to the persona.
2. **Retrieval.** The system searches the persona's knowledge base and pulls back the
   most relevant passages.
3. **Generation.** The language model writes an answer *using those passages*, guided by
   the persona's instructions.
4. **Citations.** The passages that informed the answer are returned alongside it, so
   you can click through and verify where each claim came from.

This is why a well-configured persona can answer "What are your weekend hours?" from your
own policy document — and show you the exact line it used.

## "Grounding": answering from your documents

Grounding means keeping the persona's answers tied to retrieved passages rather than to
the model's general knowledge. Grounding isn't a single on/off switch — it's the result
of a few settings working together:

- **The persona's instructions** tell it to answer from the provided context and to say
  when it doesn't know.
- **How selective retrieval is** controls how relevant a passage must be before it's
  used, and how many passages are considered.
- **Which knowledge base it points at** decides what documents are even in scope.
- **How creative the model is allowed to be** — lower creativity keeps it closer to the
  source.

The how-to guides walk through tuning each of these for your own persona.

## Good to know

- **Citations are a feature, not a guarantee.** They show what the persona retrieved.
  Tightening the settings above makes answers stick closer to those sources.
- **No knowledge base, no grounding.** A persona with nothing to retrieve from falls back
  on the model's general knowledge — useful for chit-chat, not for answering about your
  business.
